import clsx from "clsx"
import {GatsbyImage, IGatsbyImageData} from "gatsby-plugin-image"
import {Children, isValidElement, useState} from "react"
import DotsSelect from "~/components/UI/DotsSelect/DotsSelect"
import Fader from "~/components/UI/Fader/Fader"
import MultiStyleText from "~/components/UI/MultiStyleText/MultiStyleText"
import Numbers from "~/components/UI/Numbers/Numbers"
import RichTextField from "~/components/UI/RichText/RichText"
import MobileVideoPlayer from "~/components/UI/VideoPlayer/MobileVideoPlayer"
import {VideoSource} from "~/types/VideoSource"
import LinkButton from "../../UI/Button/LinkButton"
import "./Carousel.css"

interface CarouselProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
    title: string | undefined | null
    backgroundImage: IGatsbyImageData | undefined | null
    backgroundAlt: string | undefined | null
    noVideo?: boolean
    ctaName?: string | undefined
    ctaLink?: string | undefined
}

interface CarouselComponents {
    Slide: React.FC<CarouselSlideProps>
}

const Carousel: React.FC<CarouselProps> & CarouselComponents = props => {
    const {className, children, title, noVideo = false, backgroundImage, backgroundAlt, ...otherProps} = props
    const [selected, setSelected] = useState(0)

    const slides: Array<React.ReactElement<CarouselSlideProps>> = []
    Children.map(children, child => {
        if (!isValidElement<CarouselSlideProps>(child)) return
        slides.push(child)
    })

    return (
        <div className={clsx("carousel-t99q", className)} {...otherProps}>
            <MultiStyleText className="title-t99q" className1="title-part-1-t99q" className2="title-part-2-t99q">
                {title}
            </MultiStyleText>
            <div className="left-t99q">
                {backgroundImage && backgroundAlt && (
                    <GatsbyImage alt={backgroundAlt} image={backgroundImage} className="image-wrapper-t99q" />
                )}
                <Fader selected={selected} onSelect={setSelected} className="video-fader-t99q">
                    {slides.map((slide, index) => {
                        return (
                            <Fader.Slide key={index}>
                                {slide.props.video?.src && (
                                    <MobileVideoPlayer
                                        videoClass="mobile-video-player-t99q"
                                        pauseVideo={index !== selected}
                                        videoSources={[
                                            {
                                                src: slide.props.video.src,
                                                mimeType: slide.props.video.mimeType,
                                            },
                                        ]}
                                    />
                                )}
                            </Fader.Slide>
                        )
                    })}
                </Fader>
            </div>
            <div className={clsx("right-t99q", noVideo ? "noVideo-t99q" : "")}>
                <Fader selected={selected} onSelect={setSelected} slideClass="slide-t99q">
                    {slides.map((slide, index) => {
                        return (
                            <Fader.Slide key={index}>
                                <h2 className="sub-title-t99q">{slide.props.title}</h2>
                                <RichTextField className="description-t99q">{slide.props.description}</RichTextField>
                                <div className="addons-t99q">
                                    {slide.props.chart &&
                                        slide.props.chart.image?.gatsbyImageData &&
                                        slide.props.chart.title && (
                                            <>
                                                <GatsbyImage
                                                    className="chart-t99q"
                                                    alt={slide.props.chart.imageDescription ?? ""}
                                                    image={slide.props.chart.image?.gatsbyImageData}
                                                />
                                                <RichTextField>{slide.props.chart.content?.raw}</RichTextField>
                                            </>
                                        )}
                                    <Numbers>
                                        {slide.props.dataList?.map((item, index) => {
                                            return (
                                                <Numbers.Item
                                                    key={index}
                                                    value={item?.value}
                                                    description={item?.description}
                                                />
                                            )
                                        })}
                                    </Numbers>
                                </div>
                                {slide.props.ctaLink && slide.props.ctaName && (
                                    <LinkButton className="cta-t99q" to={slide.props.ctaLink}>
                                        {slide.props.ctaName}
                                    </LinkButton>
                                )}
                            </Fader.Slide>
                        )
                    })}
                </Fader>
                <DotsSelect length={slides.length} selected={selected} onSelect={setSelected} className="dots-t99q" />
            </div>
        </div>
    )
}

export default Carousel

interface CarouselSlideProps {
    readonly title: string | undefined | null | undefined
    readonly description: string | undefined | null | undefined
    readonly ctaName?: string | undefined | null | undefined
    readonly ctaLink?: string | undefined | null | undefined
    readonly video: VideoSource | null | undefined
    readonly dataList?: ReadonlyArray<Content | null> | null | undefined
    readonly chart?: Content | null | undefined
}

type Content = {
    readonly title?: string | null | undefined
    readonly value?: string | null | undefined
    readonly description?: string | null | undefined
    readonly imageDescription?: string | null | undefined
    readonly content?: {readonly raw?: string | null} | null | undefined
    readonly image?:
        | {
              readonly gatsbyImageData?: import("gatsby-plugin-image").IGatsbyImageData | null
          }
        | null
        | undefined
}

const CarouselSlide: React.FC<CarouselSlideProps> = props => null

Carousel.Slide = CarouselSlide
